import { Piece } from '../general-types/briefs.dto';

/** The stored rates a brief price carries, in pence. */
export type StoredRateField =
  | 'facePrice'
  | 'ugcPrice'
  | 'advertisingPrice'
  | 'socialPostPrice'
  | 'fifteenSecPrice'
  | 'thirtySecPrice'
  | 'sixtySecPrice';

export type StoredRates = Partial<Record<StoredRateField, number | null>> & {
  adLicenseSelected?: boolean | null;
};

export type PieceRateUsage = {
  /** The rates at least one piece is priced from. */
  used: ReadonlySet<StoredRateField>;
  /** Content plus duration rate for each piece, in pence, before rank and audience fees. */
  perPiece: number[];
};

const DURATION_FIELD: Record<Piece['durationSeconds'], StoredRateField> = {
  15: 'fifteenSecPrice',
  30: 'thirtySecPrice',
  60: 'sixtySecPrice',
};

/**
 * Which stored rates a piece brief actually uses, and each piece's base price.
 *
 * A piece brief stores every rate from the pricing model, so "rate > 0" no longer says
 * which ones apply, and adding them all up overstates the price. Same choice as the
 * API's piece engine (`pieceRateCard` / `quotePieceHire`): a piece that posts uses the
 * social rate; a UGC-only piece uses the advertising rate when the licence was bought,
 * otherwise the UGC rate; every piece adds its own duration's rate.
 */
export function pieceRateUsage(pieces: readonly Piece[], price: StoredRates | null | undefined): PieceRateUsage {
  const licensed = price?.adLicenseSelected != null
    ? price.adLicenseSelected
    : (price?.ugcPrice ?? 0) === 0 && (price?.advertisingPrice ?? 0) > 0;
  const used = new Set<StoredRateField>();
  const perPiece = pieces.map((piece) => {
    const posts = piece.placements.some((placement) => placement.kind === 'social');
    const content: StoredRateField = posts ? 'socialPostPrice' : licensed ? 'advertisingPrice' : 'ugcPrice';
    const duration = DURATION_FIELD[piece.durationSeconds] ?? 'thirtySecPrice';
    used.add(content);
    used.add(duration);
    return Math.max(0, price?.[content] ?? 0) + Math.max(0, price?.[duration] ?? 0);
  });
  return { used, perPiece };
}
